import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { Mail, AlertTriangle, ArrowLeft } from "lucide-react";

// Admin → Email gallery: every automated email rendered with sample data (read-only, no sends).
export default function AdminEmailPreviewPage() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [active, setActive] = useState(null);
  const [q, setQ] = useState("");

  useEffect(() => {
    if (!loading && (!user || user.role !== "admin")) navigate("/", { replace: true });
  }, [user, loading, navigate]);

  useEffect(() => {
    if (!user || user.role !== "admin") return;
    api.get("/admin/email-gallery").then(({ data }) => { setItems(data.items); setActive(data.items[0]?.key || null); }).catch(() => setItems([]));
  }, [user]);

  const list = (items || []).filter((t) => !q.trim() || `${t.name} ${t.audience} ${t.subject}`.toLowerCase().includes(q.toLowerCase()));
  const current = (items || []).find((t) => t.key === active) || null;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="admin-email-preview-page">
      <button type="button" onClick={() => navigate("/admin")} className="text-xs text-[#7D8C85] hover:text-[#264839] inline-flex items-center gap-1 mb-4" data-testid="email-gallery-back"><ArrowLeft className="w-3.5 h-3.5" /> Back to Admin</button>
      <div className="tracking-overline text-[#B35C4A] mb-2">Admin · Email gallery</div>
      <h1 className="font-serif text-4xl md:text-5xl text-[#11211A] tracking-tight mb-2">Every automated email, at a glance</h1>
      <p className="text-sm text-[#7D8C85] max-w-2xl mb-8">Rendered live from the same templates production uses, with sample data filled in. Nothing is sent. {items ? `${items.length} templates.` : ""}</p>

      {!items ? <div className="text-sm text-[#7D8C85]">Loading templates…</div> : (
        <div className="grid lg:grid-cols-[320px_1fr] gap-6">
          <aside className="bg-white border border-[#E8ECE9]" data-testid="email-gallery-list">
            <div className="p-3 border-b border-[#E8ECE9]">
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter templates…" className="w-full px-3 py-2 border border-[#C4CFC9] text-sm" data-testid="email-gallery-filter" />
            </div>
            <ul className="max-h-[70vh] overflow-y-auto divide-y divide-[#F0EFEA]">
              {list.map((t) => (
                <li key={t.key}>
                  <button type="button" onClick={() => setActive(t.key)} className={`w-full text-left px-4 py-3 transition ${active === t.key ? "bg-[#264839] text-white" : "hover:bg-[#FAFAF7]"}`} data-testid={`email-gallery-item-${t.key}`}>
                    <div className="text-sm font-medium flex items-center gap-2">{t.error ? <AlertTriangle className="w-3.5 h-3.5 text-[#B35C4A]" /> : <Mail className={`w-3.5 h-3.5 ${active === t.key ? "text-[#F0C866]" : "text-[#264839]"}`} />} {t.name}</div>
                    <div className={`text-[11px] mt-0.5 ${active === t.key ? "text-white/70" : "text-[#7D8C85]"}`}>To: {t.audience}</div>
                  </button>
                </li>
              ))}
              {list.length === 0 && <li className="px-4 py-6 text-sm text-[#7D8C85]">No templates match.</li>}
            </ul>
          </aside>

          <section>
            {current && (
              <>
                <div className="bg-[#F0EFEA] border-l-4 border-[#264839] p-4 mb-4" data-testid="email-gallery-meta">
                  <div className="text-[10px] uppercase tracking-widest text-[#7D8C85]">Subject</div>
                  <div className="font-serif text-lg text-[#11211A]" data-testid="email-gallery-subject">{current.subject}</div>
                  <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-[#4A5550] mt-2">
                    <span><span className="text-[#7D8C85]">Audience:</span> {current.audience}</span>
                    {current.trigger && <span><span className="text-[#7D8C85]">Sent when:</span> {current.trigger}</span>}
                    {current.optout && (
                      <span data-testid="email-gallery-optout">
                        <span className="text-[#7D8C85]">Opt-out:</span>{" "}
                        <span className={current.optout.status === "none" ? "text-[#4A5550]" : "text-[#264839] font-medium"}>
                          {current.optout.status === "added" ? "One-click unsubscribe + reply STOP (added)" : current.optout.status === "existing" ? "One-click unsubscribe + reply STOP" : "None — transactional"}
                        </span>
                        {current.optout.note && <span className="text-[#7D8C85]"> · {current.optout.note}</span>}
                      </span>
                    )}
                  </div>
                </div>
                {current.error ? (
                  <div className="bg-[#FBEAE6] border border-[#B35C4A]/40 p-4 text-sm text-[#B35C4A]" data-testid="email-gallery-error">This template failed to render: {current.error}</div>
                ) : (
                  <div className="bg-[#E8ECE9] p-4 border border-[#C4CFC9]" data-testid="email-gallery-frame">
                    <iframe title={`Preview: ${current.name}`} srcDoc={current.html} sandbox="" className="w-full bg-white border-0" style={{ minHeight: "720px" }} data-testid="email-gallery-iframe" />
                  </div>
                )}
              </>
            )}
          </section>
        </div>
      )}
    </div>
  );
}
